import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useSelection } from '@/contexts/SelectionContext';
import { useProductStats } from '@/hooks/useProductStats';
import { useEnforcement } from '@/hooks/useEnforcement';
import { useLinkedAdsAccount } from '@/hooks/useLinkedAdsAccount';
import { useAdsAccounts } from '@/hooks/useAdsPerformance';
import { useAdsFeedSummary } from '@/hooks/useAdsFeedSummary';
import { useLiveFeedStatus } from '@/hooks/useLiveFeedStatus';
import { deriveAdsChapterState, isAdsLinkedState } from '@/lib/adsFeedSummary';
import { useMcViewerRole } from '@/hooks/useMcViewerRole';
import { supabase } from '@/integrations/supabase/client';
import {
  ADS_CHAPTER_TARGET,
  LIVE_CHAPTER_TARGET,
  WORKBENCH_TO_REVIEW_TARGET,
  approveChipTarget,
} from '@/lib/journeyTargets';
import { deriveJourney, type JourneyInput, type JourneyStep, type JourneyStepId } from '@/lib/journey';

// Zwei Hook-Instanzen auf einer Seite (Layout-Band und Dashboard) lesen
// dieselbe Query: derselbe Fehler geht so nur einmal an Sentry.
const gemeldeteFehler = new WeakSet<object>();

export interface JourneyResult {
  steps: JourneyStep[];
  /**
   * Der naechste Schritt der LEITER. Leser sind `ContextJourneyBand` (reicht
   * ihn an die rein darstellenden `JourneyChips` weiter) und alles, was den
   * Fortschritt beschreibt. Er kippt nie (Entscheidung D1 in journey.ts).
   */
  nextStep: JourneyStepId | null;
  /**
   * Die Primaeraktion der Handlungskarte des Feed-Checks -- und NUR die. Nicht
   * die Chips: die lesen `nextStep`.
   *
   * Weicht von `nextStep` genau dann ab, wenn die offenen Freigaben ein
   * Restposten neben der optimierbaren Menge sind (`APPROVE_FIRST_MAX_RATIO` in
   * journey.ts); dann steht hier 'optimize', waehrend die Leiter weiterhin die
   * Freigabe vorn fuehrt.
   */
  actionStep: JourneyStepId | null;
  /**
   * Der Schritt, der seine Vorfahrt an `actionStep` abgegeben hat, als
   * Zweitaktion derselben Handlungskarte. `null` heisst "es gibt keine
   * Zweitaktion", nicht "die Zweitaktion ist leer": der Restposten soll
   * erreichbar bleiben, statt aus der Karte zu verschwinden.
   */
  actionSecondaryStep: JourneyStepId | null;
  /** N4: Kontingent erschoepft. Der Zeiger bleibt auf Optimieren, das Ziel wird /pricing. */
  quotaBlocked: boolean;
  /**
   * Mindestens einer der beiden Auto-Upload-Haken steht
   * (`auto_upload_new_enabled` / `auto_upload_drift_enabled`).
   *
   * Nach aussen gegeben, weil eine Zusage wie "Du prüfst jede Änderung in der
   * Werkbank, bevor irgendetwas zu Google geht" (`feedCheck.cta.noCard`) bei
   * gesetztem Haken unwahr ist: die Einstellungen sagen an derselben Checkbox
   * ausdruecklich "Ohne Zwischenschritt in der Werkbank. Die KI-Optimierungen
   * gehen ungeprüft live" (settings.automation.autoUploadHint). Wer die Zusage
   * anzeigt, braucht deshalb diesen Zustand.
   *
   * ACHTUNG, es bleibt eine ABSICHT, kein Zustand (siehe `JourneyInput.autoUpload`):
   * serverseitig greift der Auto-Upload nur fuer Automatik-Laeufe. Ein manuell
   * ausgeloester Lauf geht weiterhin durch die Werkbank. Dieses Flag darf also
   * nie behaupten, DIESER Lauf werde ungeprueft hochgeladen.
   */
  autoUpload: boolean;
  /** Ziel je Schritt. Gehoert hierher und nicht in die Leiste: es haengt am
   *  Kontostand (Google-Konto verbunden?) und am Kontingent, also an Daten,
   *  die die rein darstellenden JourneyChips bewusst nicht kennen. */
  targets: Record<JourneyStepId, string>;
  /** Die speisenden Quellen sind noch nicht aufgeloest. `selectedFeedId`
   *  steht beim Kaltstart synchron aus dem localStorage, `merchantCenter`
   *  und die COUNT-Abfragen kommen erst danach aus der DB — bis dahin waeren
   *  alle Zahlen Platzhalter-Nullen und der Zeiger stuende auf "Feed
   *  verbinden", obwohl das Konto laengst verbunden ist. Wer die Leiste
   *  rendert, zeigt solange einen Ladezustand (dasselbe Muster wie
   *  `nextStepResolved` im FeedHealthDashboard).
   *
   *  WICHTIG: das Gate haengt an `merchantCenterResolved`, NICHT an
   *  `!!merchantCenter`. Eine aufgeloeste, aber fehlende MC (serverseitig
   *  geloescht, oder der eine Ladeversuch ist gescheitert) ist KEIN
   *  Ladezustand — dann rendert die Leiste mit hasMerchantCenter=false und
   *  der Zeiger steht auf "Feed verbinden": die wahre, klickbare Aussage.
   *  Mit `!merchantCenter` stand hier sonst fuer den Rest der Session ein
   *  Skeleton, denn loadMerchantCenter laeuft nur bei ID-Wechsel erneut. */
  isLoading: boolean;
  /**
   * A2-NEU-2: die Produktzaehlung des aktuellen Feeds ist gescheitert und es
   * liegen keine Zahlen dieses Feeds vor. `isLoading` ist dann ebenfalls true
   * (die Leiter hat keine Zahlen); wer den Ladezustand zeigt, unterscheidet
   * hiermit "laedt" von "gescheitert". Ein gescheiterter Refetch mit Zahlen
   * desselben Feeds ist KEIN Fehlerzustand.
   */
  isError: boolean;
}

/**
 * Verdrahtet die eine Fortschrittsleiste mit den gemessenen Zahlen.
 *
 * Die Ableitung selbst steckt in `deriveJourney` (src/lib/journey.ts) und ist
 * eine reine Funktion — dieser Hook sammelt nur die Eingaben ein.
 */
export function useJourney(): JourneyResult {
  const { user } = useAuth();
  const { selectedFeedId, selectedGoogleAccountId, feed, merchantCenter, merchantCenterResolved } = useSelection();
  const { stats, isLoading: statsLoading, statsFeedId, isError: statsError, error: statsErrorObj } = useProductStats(selectedFeedId, false, null, null);
  const enforcement = useEnforcement();

  // Genau einmal je Fehlerobjekt an Sentry (Muster useContextTree.ts). Die
  // Seite meldet nicht zusaetzlich: sie liest dieselbe Query.
  useEffect(() => {
    if (!statsError || !statsErrorObj) return;
    if (typeof statsErrorObj !== 'object' || gemeldeteFehler.has(statsErrorObj)) return;
    gemeldeteFehler.add(statsErrorObj);
    const e = statsErrorObj as { code?: unknown; message?: unknown };
    Sentry.captureException(statsErrorObj, {
      tags: { feature: 'journey', action: 'product-stats' },
      extra: {
        feedId: selectedFeedId,
        code: typeof e.code === 'string' ? e.code : null,
        message: typeof e.message === 'string' ? e.message : null,
      },
    });
    // selectedFeedId bewusst nicht in den Deps: ein Feed-Wechsel ist kein neuer Fehler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsError, statsErrorObj]);

  // Schritt 5 der Leiter (Spec 3.1). Dieselben drei Quellen und dieselbe
  // Ableitung wie das Ads-Kapitel des Feed-Checks
  // (`FeedHealthDashboard.tsx`): ein zweites Urteil darueber, ob ein Konto
  // verknuepft ist, waere genau die Drift, die ein Chip und ein Kapitel auf
  // derselben Seite nie haben duerfen. React Query teilt die Caches, es
  // entsteht kein zusaetzlicher Netzweg.
  const { data: linkedAds, isLoading: linkedAdsLoading } = useLinkedAdsAccount(merchantCenter?.id ?? null);
  const { data: adsAccounts, isLoading: adsAccountsLoading } = useAdsAccounts();
  const hasAnyAdsAccount = !!selectedGoogleAccountId
    && (adsAccounts ?? []).some((a) => a.google_account_id === selectedGoogleAccountId);
  const { data: adsSummary, isLoading: adsSummaryLoading } = useAdsFeedSummary(
    selectedFeedId,
    linkedAds?.id ?? null,
  );
  // Mitglieder eines geteilten Merchant Centers sehen kein Ads-Konto des
  // Eigentuemers: Schritt 5 liegt dann beim Eigentuemer (A2-NEU-1).
  const viewer = useMcViewerRole(merchantCenter?.id ?? null);
  const viewerIsMember = viewer.role === 'member';
  const adsState = deriveAdsChapterState({
    hasAnyAdsAccount,
    linked: linkedAds ?? null,
    parsed: adsSummary ?? null,
    now: new Date(),
    viewerIsMember,
  });
  // NUR die Verknuepfung entscheidet ueber den Zeiger, nicht die Zahlen (Spec
  // 3.1): "Zahlen folgen" ist ein FERTIGER Schritt 5 mit Wartezustand. Waeren
  // die Zahlen Teil der Bedingung, stuende der Zeiger nach dem Verbinden noch
  // bis zum naechsten Nachtlauf auf Schritt 5 und die Karte forderte ein
  // Verbinden, das laengst passiert ist.
  const adsLinked = isAdsLinkedState(adsState);
  const adsNumbersReady = adsState === 'ready' || adsState === 'stale' || adsState === 'no_data';

  // Nur fuer den Tooltip des Live-Chips ("zuletzt hochgeladen am"). Eine Zeile
  // per indexiertem `feed_id`, 60 s gecacht.
  const live = useLiveFeedStatus(selectedFeedId);

  // Automatik-Toggles liegen auf profiles. Es gibt dafuer keinen bestehenden
  // Hook, also eine eigene Query mit langer staleTime: die beiden Flags aendern
  // sich nur, wenn der Nutzer sie in den Einstellungen umlegt.
  // Auf user.id gefiltert und im Query-Key gescoped — ohne das haelt der
  // React-Query-Cache die Flags ueber einen Nutzerwechsel im selben Tab hinweg.
  const { data: profile } = useQuery({
    queryKey: ['profile-auto-upload', user?.id],
    staleTime: 5 * 60 * 1000,
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('auto_upload_new_enabled, auto_upload_drift_enabled')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const input: JourneyInput = {
    hasMerchantCenter: !!merchantCenter,
    syncFailed: merchantCenter?.access_status === 'permission_denied',
    lastSyncedAt: feed?.last_synced_at ?? null,
    total: stats.total,
    optimized: stats.optimized,
    // Journey-Stufe 'optimize' nur, wenn der Server auch etwas auswaehlen wuerde.
    notOptimized: stats.optimizable,
    // Absichtlich weitergegeben, obwohl `deriveJourney` das Feld nicht liest
    // (siehe die Doku am Feld in journey.ts). Es ist der rohe Werkbank-Vorrat;
    // jede Stufenzahl rechnet mit der Schnittmenge darunter.
    pendingReview: stats.pendingReview,
    // Die eine gemessene Freigabe-Menge: wartet UND ist noch nicht bei Google.
    // Nicht der rohe Vorrat — `approval_status` wird nach einem Upload nie
    // zurueckgesetzt, ein Upload aus der Werkbank genuegt also, um beide Zahlen
    // dauerhaft auseinanderzutreiben (kein Automatik-Haken noetig).
    pendingReviewAwaitingUpload: stats.pendingReviewAwaitingUpload,
    // Bewusst NICHT `stats.approved`: das waere der Lebenszeit-Bestand aller je
    // freigegebenen Produkte, und der faellt nach einem Upload nie wieder auf
    // null. Siehe Doku am Feld in journey.ts.
    approved: stats.approvedAwaitingUpload,
    rejected: stats.rejected,
    uploaded: stats.uploaded,
    needsReupload: stats.needsReupload,
    autoUpload: !!(profile?.auto_upload_new_enabled || profile?.auto_upload_drift_enabled),
    canOptimize: enforcement.canOptimize,
    quotaPhase: enforcement.phase,
    adsLinked,
    adsOwnerManaged: adsState === 'owner_managed',
    adsNumbersReady,
    adsCustomerId: linkedAds?.adsCustomerId ?? null,
    lastUploadAt: live.row?.last_upload_at ?? null,
    // WP5 K22: "verbunden, aber nicht zugeordnet" ist ein eigenes Wort am Chip.
    adsAccountConnected: hasAnyAdsAccount,
  };

  const { steps, nextStep, actionStep, actionSecondaryStep, quotaBlocked } = deriveJourney(input);

  return {
    steps,
    nextStep,
    actionStep,
    actionSecondaryStep,
    quotaBlocked,
    autoUpload: input.autoUpload,
    // Spec 5.3. Zwei Regeln stecken darin, und beide sind gemessen:
    //
    // `connect` zeigt auf `/`, SOBALD ein Merchant Center da ist -- die
    // Startseite ist das Dashboard (Entscheidung D1), und ein Klick auf
    // "gesynct vor 2 Tagen" gehoert dorthin, wo der Sync-Knopf steht. Der
    // Settings-Weg bleibt der Sackgassen-Schutz fuer den Zustand davor: ohne
    // verbundenes Google-Konto ist `/settings?tab=merchants` ein Leerzustand
    // ohne primaere Aktion (MerchantCenters.tsx).
    //
    // `optimize` und `approve` tragen den Filter `scope=awaiting_upload`
    // (Spec 4, "Zahl = Liste"): die Chip-Zahl ist die Schnittmenge "wartet UND
    // ist noch nicht bei Google", und ohne den Filter oeffnete der Klick den
    // ROHEN Reiter-Vorrat. Live tragen 10.409 Zeilen gleichzeitig
    // `pending_review` und `uploaded` (5 Feeds, gemessen 31.07.2026) -- ein
    // Chip "12 zu pruefen" landete dort auf einer Liste mit 1.055 Zeilen.
    targets: {
      connect: merchantCenter
        ? '/'
        : (selectedGoogleAccountId ? '/settings?tab=merchants' : '/settings?tab=accounts'),
      optimize: quotaBlocked ? '/pricing' : WORKBENCH_TO_REVIEW_TARGET,
      // WP5 A2O-01/K23: der Chip zaehlt den freigegebenen Bestand und oeffnet
      // ihn, solange es ihn gibt. Steht der Zeiger hier, weil Produkte auf die
      // Freigabe warten, fuehrt der Chip dorthin, wo freigegeben wird.
      approve: approveChipTarget(stats.approvedAwaitingUpload, stats.pendingReviewAwaitingUpload),
      upload: LIVE_CHAPTER_TARGET,
      ads_connect: ADS_CHAPTER_TARGET,
      ads_campaigns: ADS_CHAPTER_TARGET,
    },
    // `statsLoading` allein reicht nicht: `useProductStats` haelt beim
    // Feed-Wechsel per `placeholderData` die Zahlen des VORHERIGEN Feeds und
    // meldet dabei `isPending === false`. In diesem Fenster leitete
    // `deriveJourney` Zeiger und Stufenzahlen aus dem falschen Feed ab, und die
    // Leiste stand sichtbar auf dem falschen Schritt. Der Vergleich der
    // gemessenen Feed-ID schliesst das Fenster; er ist derselbe Riegel, den
    // `FeedHealthDashboard` fuer seinen Primaer-CTA benutzt.
    // Die Aufloesung des Ads-Kontos gehoert ins Gate (Spec 3.1): ohne sie
    // zeigte Schritt 5 beim Kaltstart kurz "nicht verbunden" und der Zeiger
    // sprang von `ads_connect` auf `null`, sobald die Antwort da ist. Ein
    // Zeiger, der eine Sekunde lang eine falsche Handlung anbietet, ist genau
    // der Fall, gegen den dieses Gate gebaut ist.
    //
    // `useLiveFeedStatus` steht bewusst NICHT im Gate: es speist allein den
    // Tooltip des Live-Chips. Die Leiste auf eine Tooltip-Zeile warten zu
    // lassen, hiesse sie fuer eine Angabe zu verzoegern, die niemand sieht,
    // solange er nicht mit der Maus stehen bleibt.
    isError: statsError && statsFeedId !== selectedFeedId,
    isLoading: !selectedFeedId || !merchantCenterResolved || statsLoading
      || statsFeedId !== selectedFeedId
      || linkedAdsLoading || adsAccountsLoading || adsSummaryLoading
      || viewer.isLoading,
  };
}
